import { api } from "@/shared/api";
import type { components } from "@/lib/api/generated";
import type { Override } from "@/shared/lib/typeOverride";

// ── Types ──────────────────────────────────────────────────────────────────

// 0926 第八批 Z3:列表真返回的是 TenantListItem(旧手写 yaml 叫 TenantSummary;真 spec 里 TenantSummary
// 是平台看板统计,同名不同物)。
export type TenantListItem = Override<
  components["schemas"]["TenantListItem"],
  {
    id: number;
    code: string;
    name: string;
    contact: string;
    phone: string;
    plan: string;
    planLabel: string;
    // V27 (2026-05-20) — 行业字段, 后端 TenantListItem.from(t, industryName, ...) 透出
    primaryIndustryCode: string | null;
    primaryIndustryName: string | null;
    moduleCount: number;
    accountCount: number;
    status: string;
    statusLabel: string;
    expiryAt: string | null;
    createdAt: string;
    /** OEM A-P5:所属渠道商 code(system / null = 平台直营)。 */
    partnerCode?: string | null;
    // mark: not yet in api-spec.yaml (V24 proposal)
  }
>;

// local-only: B (backend schema gap) — see proposals/2026-05-17-fe-generated-coverage-tenant.md
// 注意:此 TenantSummary 是平台首页聚合统计(total/active/expiring 等),与 generated.ts 的
// per-tenant summary 行 (components.schemas.TenantSummary) 同名但语义不同;后者已被 TenantListItem 引用。
// V24 应改名 PlatformTenantStatsResponse 避免重名。
export interface TenantSummary {
  total: number;
  active: number;
  expiring: number;
  suspended: number;
  trial: number;
  avgModules: number;
  todayNew: number;
  expiringList: { name: string; expiryAt: string; days: number }[];
  renewList: { name: string; plan: string; expiredAt: string }[];
}

// local-only: B (backend schema gap) — TenantDetail.modules 实际 shape; yaml 现 Record<string,never>[] 是 drift
export interface ModuleItem {
  key: string;
  name: string;
  enabled: boolean;
  /**
   * 已用量。B1 0927 起 Integer 可为 null:seat / advisor / dh_voice_profile 为真实用量,其余能力 null(不适用);
   * 老后端恒 0。消费方 null 显「—」,别直接 toLocaleString(TypeError)。
   */
  usedCount: number | null;
  quota: number | null;
  /**
   * `quota == null` 的镜像 = **这一项没配额度**。
   *
   * ★2026-09-20 从 `unlimited` 改名(backend 同批):老板拍板「没有内部无限这一说」。
   * 「没配额度」是配置缺一行,「不限量」是一个承诺 —— 叫前者 unlimited,界面就会照着
   * 这个名字把它渲染成「不限」,而那句话现在一个租户都不成立。
   */
  quotaUnset: boolean;
  status: string;
  /**
   * OEM 第二批 A2:凭什么开 —— PLAN(套餐自带)/ ADDON(增值舱)/ null(未拥有)。
   * 老后端不带(undefined)时按 enabled 回落成 PLAN。
   */
  ownedBy?: "PLAN" | "ADDON" | (string & {}) | null;
  /** 已拥有但被渠道 / 平台暂停(enabled=false)。 */
  paused?: boolean;
  pausedReason?: string | null;
}

/** OEM 第二批 A4 / B6:租户管理员账号(联系方式已脱敏)。 */
export interface TenantAdminAccount {
  id: number;
  displayName: string | null;
  maskedUsername: string | null;
  /** 登录名原文(1004:登录名不脱敏,只脱敏联系方式;老后端不带 → 回退 maskedUsername)。 */
  username?: string | null;
  maskedEmail: string | null;
  maskedPhone: string | null;
  enabled: boolean;
  lastLoginAt: string | null;
}

/** 重置结果:临时口令只在这一次响应里出现(首登强制改密,旧会话全撤)。 */
export interface TenantAdminPasswordReset {
  principalId: number;
  tenantCode: string;
  tempPassword: string;
}

/** A2 改语义后的模块调整项:只能暂停 / 恢复已拥有的舱,额度只对已拥有的舱设(MANUAL)。 */
export interface ModuleAdjustItem {
  key: string;
  paused?: boolean;
  quota?: number | null;
}

// local-only: B (backend schema gap) — tenant_channels 注册表行
/** 注册表渠道行,租户详情 channels 区使用。 */
export interface ChannelItem {
  id: number;
  /** wecom | web_widget | dingtalk | feishu | whatsapp ... */
  channelType: string;
  channelTypeLabel: string;
  displayName: string;
  status: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// local-only: B (backend schema gap) — /platform/tenants/{id}/timeline 返
export interface TimelineItem {
  date: string;
  type: string;
  event: string;
  user: string;
  /** 1004 O4:对象 id(代客查看列表记 list);老后端缺省。 */
  resourceId?: string | null;
  /** 1004 O4:代客原因(只代客动作有)。 */
  reason?: string | null;
}

export type TenantDetail = Override<
  Omit<components["schemas"]["TenantDetail"], "modules">,
  {
    id: number;
    code: string;
    name: string;
    contact: string;
    phone: string;
    email: string;
    address: string;
    plan: string;
    planLabel: string;
    status: string;
    statusLabel: string;
    expiryAt: string | null;
    industryCode: IndustryCode;
    scale: string;
    remark: string;
    createdAt: string;
    modules: ModuleItem[];
    channels: ChannelItem[];
    timeline: TimelineItem[];
    accounts: { total: number; active: number };
    /**
     * 仅在 createTenant 响应中返回 — 自动创建的 TENANT_ADMIN 初始密码,
     * 展示一次后丢弃。GET / UPDATE 永远是 null/undefined。
     */
    initialPassword?: string | null;
    /**
     * OEM D1(api d01cf328):仅 createTenant 响应返回 —— 该租户应使用的登录地址(含 https://,无尾斜杠;
     * 渠道租户 = 渠道主域名,规则子域优先,形如 https://acme.oem-demo.xbwhynot.com)。
     * 直营可能为 null(回落 brand.loginOrigin);渠道租户为 null = 渠道未配访问域名(不许回落主域)。
     */
    loginUrl?: string | null;
    /** OEM 第二批 A7:所属渠道 code;直营 = "system"。老后端不带 = undefined(归属未知)。 */
    partnerCode?: string | null;
    // mark: not yet in api-spec.yaml (V24 proposal)
  }
>;

// local-only: A (frontend generic wrapper) — codegen 不出 generic;backend PageResponse 已在 yaml,但形态走不出 generic
// ★与 backend com.gscq.common.api.PageResponse 逐字对齐(2026-09-11 翻页事故):后端只回
//   {records,total,page,size} —— current/pages **从来不存在**,此前类型撒谎导致
//   `listData?.pages ?? 1` 恒 1、clamp 把翻页拍回第 1 页。消费侧 totalPages 一律用 total 自算。
export interface PageResponse<T> {
  records: T[];
  total: number;
  /** 1-based 当前页(后端字段名就叫 page)。 */
  page?: number;
  size: number;
}

// local-only: A (frontend type alias) — 仅是 string 别名,语义靠 backend xb_industry.code 注释表达,无需 yaml schema
/**
 * v1.5 (2026-05-14): IndustryCode 从枚举改为 string。
 * 权威源是 backend `xb_industry.code`(IND_* 前缀),由 industryService.list() 拉。
 * 不再硬编码 6 个老 pack(GENERIC/ECOMMERCE/...),避免新行业新增时类型阻断。
 */
export type IndustryCode = string;

// local-only: B (backend schema gap) — POST /platform/tenants body
export interface CreateTenantRequest {
  name: string;
  industryCode: IndustryCode;
  contact: string;
  phone: string;
  email?: string;
  address?: string;
  scale?: string;
  remark?: string;
  plan: string;
  expiryAt?: string;
  isTrial: boolean;
  modules?: { key: string; quota?: number }[];
}

// local-only: B (backend schema gap) — PUT /platform/tenants/{id} body
export interface UpdateTenantRequest {
  name?: string;
  industryCode?: IndustryCode;
  contact?: string;
  phone?: string;
  email?: string;
  address?: string;
  scale?: string;
  remark?: string;
  plan?: string;
  expiryAt?: string;
}

// local-only: B (backend schema gap) — /platform/tenants/{id}/modules /quotas 返
export interface TenantModule {
  id: number;
  tenantId: number;
  moduleKey: string;
  enabled: boolean;
  quota: number | null;
  /**
   * 已用量。B1(0927 第十一批)起 seat / advisor / dh_voice_profile 返真值,其余能力 null 或不带字段;
   * 老后端恒 0。消费方按「是数才显示已用」兼容(见 features/tenant-management/model/resourceCapUsage)。
   */
  usedCount?: number | null;
}

/** 平台可手工调整上限的资源项(与 api TenantAdminAppService.ADJUSTABLE_RESOURCE_CAPS 同一份)。 */
export const RESOURCE_CAP_KEYS = ["seat", "advisor", "dh_voice_profile"] as const;
export type ResourceCapKey = (typeof RESOURCE_CAP_KEYS)[number];

export function isResourceCapKey(k: string): k is ResourceCapKey {
  return (RESOURCE_CAP_KEYS as readonly string[]).includes(k);
}

// ── Service ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | undefined | null>) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "") q.set(k, String(v));
  }
  return q.toString();
}

/**
 * 租户读写接口工厂 —— basePath 可注入(OEM C-H):平台超管 = `/platform/tenants`,
 * 渠道后台 = `/partner/tenants`(后端同一套 TenantDetail / TenantListItem 契约,归属由 token 决定)。
 * ★平台端默认实例 `tenantService` 行为逐字不变;渠道端只挑它有的那几个方法用(见 entities/partner)。
 */
export function createTenantService(base: string) {
  return {
    list(
      params: {
        page?: number;
        pageSize?: number;
        search?: string;
        status?: string;
        plan?: string;
        /** OEM A-P5:按渠道商过滤(system = 平台直营)。 */
        partner?: string;
      } = {}
    ) {
      const q = buildQuery({
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        search: params.search,
        status: params.status,
        plan: params.plan,
        partner: params.partner,
      });
      return api.get<PageResponse<TenantListItem>>(`${base}?${q}`);
    },

    summary() {
      return api.get<TenantSummary>(`${base}/summary`);
    },

    /** partnerCode:OEM A-P5 给渠道开户(走 ?partnerCode=;空 / system = 平台直营,请求体不变)。 */
    create(req: CreateTenantRequest, partnerCode?: string | null) {
      const q =
        partnerCode && partnerCode !== "system"
          ? `?partnerCode=${encodeURIComponent(partnerCode)}`
          : "";
      return api.post<TenantDetail>(`${base}${q}`, req);
    },

    get(id: number | string) {
      // 兜底 backend schema gap:稀疏/pending/老租户 modules/channels 可能返 null,
      // 补 [] 避免下游 TenantQuota/Modules/Channels 区裸 .filter/.map/.length 崩。
      return api.get<TenantDetail>(`${base}/${id}`).then((d) => ({
        ...d,
        modules: d.modules ?? [],
        channels: d.channels ?? [],
        accounts: d.accounts ?? { total: 0, active: 0 },
      }));
    },

    update(id: number | string, req: UpdateTenantRequest) {
      return api.put<TenantDetail>(`${base}/${id}`, req);
    },

    /**
     * 硬删除租户:级联清 users / tenant_modules / tenant_channels(含 wecom_apps) / tenant_timeline,
     * 审计日志保留。仅给运营误建场景使用,生产数据应走"停用"。
     */
    delete(id: number | string) {
      return api.delete<void>(`${base}/${id}`);
    },

    /** 调整套餐:激活新订阅 + 重置 quota 为新 plan 的 default_buckets。 */
    adjustPlan(id: number | string, planCode: string) {
      return api.put<TenantDetail>(`${base}/${id}/plan`, { planCode });
    },

    /**
     * 代客登录(impersonation):平台超管获取一个短期 token,扮演该租户。
     * 1003:渠道客户必须带 reason(≥4 字,后端 400 IMPERSONATE_REASON_REQUIRED);直营可不带(请求体 null)。
     */
    impersonate(code: string, reason?: string) {
      return api.post<{
        accessToken: string;
        // V27 (2026-05-20) — 后端透出 tenantId,给前端代客视图(渠道管理 detail / 工作台
        // tenant_dashboard 等需要带 id 的接口)直接用,不必再发一次 lookup-by-code。
        tenantId: number;
        tenantCode: string;
        tenantName: string;
        expiresInMs: number;
      }>(`${base}/${encodeURIComponent(code)}/impersonate`, reason ? { reason } : null);
    },

    updateStatus(id: number | string, status: string, reason?: string) {
      return api.put<void>(`${base}/${id}/status`, { status, reason });
    },

    /**
     * 重新铺行业资料(1002 行业包补全):按租户当前行业码(及其父码)幂等补铺客服场景标签与陪练预置;
     * 旧的不删、重复调用不重复插。仅平台超管(后端落 industry_reprovision 审计)。
     */
    reprovisionIndustry(id: number | string) {
      return api.post<{ tenantCode: string; industryCode: string }>(
        `${base}/${id}/industry-reprovision`,
        null
      );
    },

    getModules(id: number | string) {
      return api.get<TenantModule[]>(`${base}/${id}/modules`);
    },

    /**
     * OEM 第二批 A2:每项 {key, paused?, quota?};打开未拥有的舱 → 409 MODULE_NOT_OWNED(请用「赠送增值舱」)。
     * 渠道租户 reason 必填(落暂停原因 + 审计)。返回调整后的模块行。
     */
    updateModules(id: number | string, modules: ModuleAdjustItem[], reason?: string | null) {
      return api.put<ModuleItem[]>(`${base}/${id}/modules`, { modules, reason: reason || null });
    },

    /** A4 / B6:该租户的 TENANT_ADMIN 列表(脱敏)。 */
    listAdmins(id: number | string) {
      return api.get<TenantAdminAccount[]>(`${base}/${id}/admins`);
    },

    /** A4 / B6:重置租户管理员密码,临时口令一次性返回。 */
    resetAdminPassword(id: number | string, principalId: number) {
      return api.post<TenantAdminPasswordReset>(
        `${base}/${id}/admins/${principalId}/reset-password`,
        null
      );
    },

    getTimeline(id: number | string, eventType = "all") {
      return api.get<TimelineItem[]>(`${base}/${id}/timeline?eventType=${eventType}`);
    },

    getQuotas(id: number | string) {
      return api.get<TenantModule[]>(`${base}/${id}/quotas`);
    },

    /**
     * 调整资源上限(席位 seat / 军师授权 advisor / 声纹条数 dh_voice_profile;修复波 0926 第八批 Z1)。
     * 只改额度不动开通状态;quota 必须是正整数(不提供「不设上限」,null 后端 400);reason 必填,后端写进 update_quotas 审计。
     */
    updateQuotas(
      id: number | string,
      quotas: { key: ResourceCapKey; quota: number }[],
      reason: string
    ) {
      return api.put<TenantModule[]>(`${base}/${id}/quotas`, { quotas, reason });
    },
  };
}

export const tenantService = createTenantService("/platform/tenants");
